import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLoad, act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { PageHeader, Section, Field, StackTable } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const LABEL = { P1: "P1 · Critical", P2: "P2 · High", P3: "P3 · Medium / Low" };

function HoursInput({ p, k, f, setF, disabled }) {
  return <Input type="number" min="0.25" step="0.25" className="h-9 w-28" disabled={disabled} data-testid={`sla-${p}-${k}`}
    value={f.targets[p][k]} onChange={(e) => setF({ ...f, targets: { ...f.targets, [p]: { ...f.targets[p], [k]: e.target.value } } })} />;
}

export default function SlaSettings() {
  const { user } = useAuth();
  const canEdit = ["platform_ops", "platform_admin"].includes(user?.role);
  const [d, reload] = useLoad("/support/sla");
  const [f, setF] = useState(null);
  useEffect(() => { if (d) setF({ targets: d.targets, at_risk_pct: d.at_risk_pct }); }, [d]);
  if (!f) return null;
  const save = async (body) => { if (await act(() => api.put("/support/sla", body), "SLA targets saved")) reload(); };
  const toBody = (x) => ({ at_risk_pct: Number(x.at_risk_pct), targets: Object.fromEntries(Object.entries(x.targets).map(([p, t]) => [p, { first_response_hours: Number(t.first_response_hours), resolution_hours: Number(t.resolution_hours) }])) });
  return (
    <div data-testid="sla-settings-page">
      <PageHeader title="SLA targets" subtitle="How fast the support team must first respond to and resolve tickets for each priority. Changes apply right away to every open ticket's SLA clock." />
      {!canEdit && <p className="mb-3 text-xs text-slate-500" data-testid="sla-readonly-note">Only Ops admins can change these targets.</p>}
      <Section title="Targets per priority (hours)">
        <div className="overflow-x-auto"><StackTable data-testid="sla-table"><thead><tr><th>Priority</th><th>First response</th><th>Resolution</th><th>Default</th></tr></thead>
          <tbody>{Object.keys(LABEL).map((p) => (
            <tr key={p} data-testid={`sla-row-${p}`}><td className="font-medium">{LABEL[p]}</td>
              <td><HoursInput p={p} k="first_response_hours" f={f} setF={setF} disabled={!canEdit} /></td>
              <td><HoursInput p={p} k="resolution_hours" f={f} setF={setF} disabled={!canEdit} /></td>
              <td className="text-xs text-slate-500">{d.defaults[p].first_response_hours}h / {d.defaults[p].resolution_hours}h</td></tr>))}</tbody></StackTable></div>
        <div className="flex flex-wrap items-end gap-3 p-5">
          <Field label="Mark “at risk” when this % of the target is used"><Input type="number" min="10" max="99" className="w-28" disabled={!canEdit} data-testid="sla-risk-input" value={f.at_risk_pct} onChange={(e) => setF({ ...f, at_risk_pct: e.target.value })} /></Field>
        </div>
      </Section>
      {canEdit && <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="sla-save-button" className="bg-[#13294B] hover:bg-[#1c3a66]" onClick={() => save(toBody(f))}>Save SLA targets</Button>
        <Button variant="outline" data-testid="sla-reset-button" onClick={() => save(toBody({ targets: d.defaults, at_risk_pct: 75 }))}>Reset to defaults</Button>
      </div>}
    </div>
  );
}
